/**
 * Relative time formatting — single source of truth for "how long ago" strings
 * across the dashboard.
 *
 * Accepts a millisecond timestamp, ISO string, or `Date`. Returns short
 * human-friendly strings ("just now", "3m ago", "5d ago", "2w ago").
 */

function toMs(input: number | string | Date): number {
  return typeof input === 'number' ? input : input instanceof Date ? input.getTime() : Date.parse(input);
}

export function relativeTime(input: number | string | Date): string {
  const ts = toMs(input);
  if (!Number.isFinite(ts)) return '';

  const diff = Date.now() - ts;
  if (diff < 5_000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1_000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / (7 * 86_400_000))}w ago`;
  if (diff < 365 * 86_400_000) return `${Math.floor(diff / (30 * 86_400_000))}mo ago`;
  return `${Math.floor(diff / (365 * 86_400_000))}y ago`;
}

/**
 * Compact variant — no "ago" suffix, falls through to a calendar date past 7d.
 * Used by inbox-style lists where the column is narrow and we'd rather show
 * the exact day for older items than a fuzzy "3w".
 */
export function shortRelativeTime(input: number | string | Date): string {
  const ts = toMs(input);
  if (!Number.isFinite(ts)) return '';

  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
